/**
 * MUTAGIM.SHOP — Phase 16: Social Media Studio
 * Page admin pour créer des contenus publicitaires
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Instagram, Facebook, Video, Image, Sparkles, Copy, Send, 
  Download, RefreshCw, Calendar, History, Settings, ChevronLeft,
  Loader2, Check, Eye, Wand2, Hash, MessageSquare, Palette
} from 'lucide-react';
import { AdminPageWrapper } from '../components/admin/AdminPageWrapper';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper pour formater les URLs d'images
const getImageUrl = (img) => {
  if (!img) return '/placeholder.jpg';
  
  // Si c'est un objet, extraire l'URL
  if (typeof img === 'object') {
    img = img.url || img.src || img.main?.[0] || img[0] || null;
    if (!img) return '/placeholder.jpg';
  }
  
  if (typeof img !== 'string') return '/placeholder.jpg';
  
  if (img.startsWith('http')) return img;
  if (img.startsWith('/api/')) return `${API_URL}${img}`;
  if (img.startsWith('/uploads/')) return `${API_URL}/api${img}`;
  return `${API_URL}/api/uploads/${img}`;
};

// Extraire la première image d'un produit
const getProductImage = (product) => {
  if (!product) return '/placeholder.jpg';
  
  const images = product.images;
  if (!images) return '/placeholder.jpg';
  
  // Si c'est un tableau
  if (Array.isArray(images)) {
    return getImageUrl(images[0]);
  }
  
  // Si c'est un objet avec main
  if (images.main) {
    if (Array.isArray(images.main)) {
      return getImageUrl(images.main[0]);
    }
    return getImageUrl(images.main);
  }
  
  return '/placeholder.jpg';
};

// Formats publicitaires
const AD_FORMATS = [
  { id: 'square', label: '1:1 Carré', width: 1080, height: 1080, platforms: ['Instagram', 'Facebook'] },
  { id: 'story', label: '9:16 Story', width: 1080, height: 1920, platforms: ['Instagram', 'TikTok', 'Snapchat'] },
  { id: 'banner', label: '16:9 Bannière', width: 1920, height: 1080, platforms: ['Facebook', 'Web'] },
  { id: 'portrait', label: '4:5 Portrait', width: 1080, height: 1350, platforms: ['Instagram'] },
];

const PLATFORMS = [
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: 'bg-gradient-to-br from-purple-500 to-pink-500' },
  { id: 'facebook', name: 'Facebook', icon: Facebook, color: 'bg-blue-600' },
  { id: 'tiktok', name: 'TikTok', icon: Video, color: 'bg-black' },
];

function getToken() {
  return localStorage.getItem('admin_token') || localStorage.getItem('auth_token');
}

export function AdminSocialPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Data states
  const [products, setProducts] = useState([]);
  const [atmospheres, setAtmospheres] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Studio states
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedAtmosphere, setSelectedAtmosphere] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('square');
  const [slogan, setSlogan] = useState('Style Authentique');
  const [promoCode, setPromoCode] = useState('');
  const [language, setLanguage] = useState('fr');
  
  // Generated content
  const [visualHtml, setVisualHtml] = useState('');
  const [copyData, setCopyData] = useState(null);
  const [selectedHook, setSelectedHook] = useState('a');
  
  // Publication states
  const [selectedPlatforms, setSelectedPlatforms] = useState(['instagram']);
  const [scheduledDate, setScheduledDate] = useState('');
  
  // Loading states
  const [generatingVisual, setGeneratingVisual] = useState(false);
  const [generatingCopy, setGeneratingCopy] = useState(false);
  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Auth check
  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    const token = getToken();
    try {
      // Fetch products (limit to 30 to avoid memory issues)
      const productsRes = await fetch(`${API_URL}/api/products?limit=30`);
      if (productsRes.ok) {
        const data = await productsRes.json();
        setProducts(data.products || []);
      }

      // Fetch atmospheres
      const atmRes = await fetch(`${API_URL}/api/admin/atmospheres`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (atmRes.ok) {
        const data = await atmRes.json();
        setAtmospheres(data.atmospheres || []);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Générer le visuel
  const generateVisual = async () => {
    if (!selectedProduct) {
      toast.error('Sélectionnez un produit');
      return;
    }

    setGeneratingVisual(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/social/generate-visual`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_id: selectedProduct,
          atmosphere_id: selectedAtmosphere || null,
          format: selectedFormat,
          slogan,
          promo_code: promoCode || null,
          language,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setVisualHtml(data.visual.html_template);
        toast.success('Visuel généré !');
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast.error('Erreur lors de la génération');
    } finally {
      setGeneratingVisual(false);
    }
  };

  // Générer le copy IA
  const generateCopy = async () => {
    if (!selectedProduct) {
      toast.error('Sélectionnez un produit');
      return;
    }

    setGeneratingCopy(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/social/generate-copy`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_id: selectedProduct,
          language,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setCopyData(data.copy);
        toast.success('Copy généré par Claude !');
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast.error('Erreur lors de la génération');
    } finally {
      setGeneratingCopy(false);
    }
  };

  // Générer une vidéo
  const generateVideo = async () => {
    if (!selectedProduct) {
      toast.error('Sélectionnez un produit');
      return;
    }

    setGeneratingVideo(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/social/generate-video`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_id: selectedProduct,
          duration: 4,
          effect: 'zoom',
        }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.mock) {
          toast.info('Vidéo simulée (mode MOCK)');
        } else {
          toast.success('Vidéo générée !');
        }
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast.error('Erreur lors de la génération');
    } finally {
      setGeneratingVideo(false);
    }
  };

  // Publier le post
  const publishPost = async () => {
    if (!visualHtml || !copyData || selectedPlatforms.length === 0) {
      toast.error('Générez d\'abord le visuel et le copy, puis sélectionnez au moins une plateforme');
      return;
    }

    setPublishing(true);
    try {
      const token = getToken();
      
      // Créer le post
      const createRes = await fetch(`${API_URL}/api/social/posts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_id: selectedProduct,
          format: selectedFormat,
          visual_url: '/generated-visual.png', // Placeholder
          copy_hook: selectedHook === 'a' ? copyData.hook_a : copyData.hook_b,
          copy_body: copyData.body,
          hashtags: copyData.hashtags,
          platforms: selectedPlatforms,
          scheduled_at: scheduledDate ? new Date(scheduledDate).toISOString() : null,
        }),
      });

      if (!createRes.ok) throw new Error('Failed to create post');
      const postData = await createRes.json();

      // Si pas de date planifiée, publier immédiatement
      if (!scheduledDate) {
        const publishRes = await fetch(`${API_URL}/api/social/posts/${postData.post.id}/publish`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (publishRes.ok) {
          const result = await publishRes.json();
          if (result.mock) {
            toast.info('Publication simulée (mode MOCK)');
          } else {
            toast.success('Publié avec succès !');
          }
        }
      } else {
        toast.success('Post planifié !');
      }
    } catch (error) {
      toast.error('Erreur lors de la publication');
    } finally {
      setPublishing(false);
    }
  };

  // Toggle platform selection
  const togglePlatform = (platformId) => {
    setSelectedPlatforms(prev => 
      prev.includes(platformId) 
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    );
  };

  // Get selected product data
  const selectedProductData = products.find(p => p.id === selectedProduct);
  const currentFormat = AD_FORMATS.find(f => f.id === selectedFormat);

  if (loading) {
    return (
      <AdminPageWrapper title="Studio Social" subtitle="Chargement...">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-mutagim-red" />
        </div>
      </AdminPageWrapper>
    );
  }

  return (
    <AdminPageWrapper title="Studio Social" subtitle="Créez vos contenus publicitaires">
      <div className="space-y-8" data-testid="admin-social-page">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Configuration */}
          <div className="space-y-6">
            {/* Product Selection */}
            <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Image className="w-5 h-5 text-purple-400" />
                    1. Sélectionner un produit
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="product-selector">
                      <SelectValue placeholder="Choisir un produit..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {products.filter(p => p.id).map((product) => (
                        <SelectItem key={product.id} value={product.id}>
                          <span>{product.name?.fr || product.name?.en || product.slug || 'Produit sans nom'}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>

              {/* Atmosphere Selection */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Palette className="w-5 h-5 text-pink-400" />
                    2. Atmosphère (optionnel)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Select value={selectedAtmosphere} onValueChange={setSelectedAtmosphere}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="atmosphere-selector">
                      <SelectValue placeholder="Aucune atmosphère" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune</SelectItem>
                      {atmospheres.filter(a => a.id).map((atm) => (
                        <SelectItem key={atm.id} value={atm.id}>
                          {typeof atm.name === 'string' ? atm.name : (atm.name?.fr || atm.preset)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>

              {/* Format Selection */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Image className="w-5 h-5 text-blue-400" />
                    3. Format
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {AD_FORMATS.map((format) => (
                      <button
                        key={format.id}
                        onClick={() => setSelectedFormat(format.id)}
                        className={cn(
                          "p-4 rounded-lg border-2 text-left transition-all",
                          selectedFormat === format.id
                            ? "border-purple-500 bg-purple-500/10"
                            : "border-zinc-700 hover:border-zinc-600"
                        )}
                        data-testid={`format-${format.id}`}
                      >
                        <p className="text-white font-medium">{format.label}</p>
                        <p className="text-xs text-white">{format.width}×{format.height}</p>
                        <div className="flex gap-1 mt-2">
                          {format.platforms.map((p) => (
                            <Badge key={p} variant="secondary" className="text-xs">
                              {p}
                            </Badge>
                          ))}
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Customization */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-green-400" />
                    4. Personnalisation
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Slogan / Bénéfice client</Label>
                    <Input
                      value={slogan}
                      onChange={(e) => setSlogan(e.target.value)}
                      placeholder="Style Authentique"
                      className="bg-zinc-800 border-zinc-700"
                      data-testid="slogan-input"
                    />
                  </div>
                  <div>
                    <Label>Code promo Crew (optionnel)</Label>
                    <Input
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value)}
                      placeholder="CREW-XXXX"
                      className="bg-zinc-800 border-zinc-700"
                      data-testid="promo-code-input"
                    />
                  </div>
                  <div>
                    <Label>Langue</Label>
                    <Select value={language} onValueChange={setLanguage}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fr">Français</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="he">עברית</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Generation Buttons */}
              <div className="flex gap-3">
                <Button 
                  onClick={generateVisual}
                  disabled={generatingVisual || !selectedProduct}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  data-testid="generate-visual-btn"
                >
                  {generatingVisual ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Image className="w-4 h-4 mr-2" />
                  )}
                  Générer Image
                </Button>
                <Button 
                  onClick={generateVideo}
                  disabled={generatingVideo || !selectedProduct}
                  variant="outline"
                  className="flex-1"
                  data-testid="generate-video-btn"
                >
                  {generatingVideo ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Video className="w-4 h-4 mr-2" />
                  )}
                  Générer Vidéo
                </Button>
              </div>
            </div>

            {/* Right: Preview & Copy */}
            <div className="space-y-6">
              {/* Visual Preview */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Eye className="w-5 h-5 text-cyan-400" />
                    Prévisualisation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div 
                    className="bg-zinc-800 rounded-lg overflow-hidden flex items-center justify-center"
                    style={{
                      aspectRatio: currentFormat ? `${currentFormat.width}/${currentFormat.height}` : '1/1',
                      maxHeight: '500px'
                    }}
                  >
                    {visualHtml ? (
                      <iframe
                        srcDoc={visualHtml}
                        className="w-full h-full border-0"
                        title="Visual Preview"
                        sandbox="allow-same-origin"
                      />
                    ) : selectedProductData ? (
                      <div className="text-center p-8">
                        <img 
                          src={getProductImage(selectedProductData)}
                          alt=""
                          className="max-h-64 mx-auto mb-4 rounded-lg"
                          onError={(e) => { e.target.src = '/placeholder.jpg'; }}
                        />
                        <p className="text-white font-medium">{selectedProductData.name?.fr || selectedProductData.name?.en}</p>
                        <p className="text-white text-sm mt-2">
                          Cliquez sur "Générer Image" pour créer le visuel
                        </p>
                      </div>
                    ) : (
                      <p className="text-gray-200">Sélectionnez un produit</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Copy Generation */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Wand2 className="w-5 h-5 text-yellow-400" />
                    Copy IA (Claude)
                  </CardTitle>
                  <Button 
                    onClick={generateCopy}
                    disabled={generatingCopy || !selectedProduct}
                    size="sm"
                    data-testid="generate-copy-btn"
                  >
                    {generatingCopy ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    Générer Copy
                  </Button>
                </CardHeader>
                <CardContent>
                  {copyData ? (
                    <div className="space-y-4">
                      {/* Hook Selection (A/B) */}
                      <div>
                        <Label className="mb-2 block">Accroche (A/B Testing)</Label>
                        <div className="space-y-2">
                          <button
                            onClick={() => setSelectedHook('a')}
                            className={cn(
                              "w-full p-3 rounded-lg text-left transition-all",
                              selectedHook === 'a'
                                ? "bg-yellow-500/20 border-2 border-yellow-500"
                                : "bg-zinc-800 border-2 border-transparent hover:border-zinc-700"
                            )}
                          >
                            <Badge className="mb-1">Variante A</Badge>
                            <p className="text-white">{copyData.hook_a}</p>
                          </button>
                          <button
                            onClick={() => setSelectedHook('b')}
                            className={cn(
                              "w-full p-3 rounded-lg text-left transition-all",
                              selectedHook === 'b'
                                ? "bg-yellow-500/20 border-2 border-yellow-500"
                                : "bg-zinc-800 border-2 border-transparent hover:border-zinc-700"
                            )}
                          >
                            <Badge className="mb-1">Variante B</Badge>
                            <p className="text-white">{copyData.hook_b}</p>
                          </button>
                        </div>
                      </div>

                      {/* Body */}
                      <div>
                        <Label className="mb-2 block">Description</Label>
                        <Textarea
                          value={copyData.body}
                          readOnly
                          className="bg-zinc-800 border-zinc-700 text-white"
                          rows={4}
                        />
                      </div>

                      {/* Hashtags */}
                      <div>
                        <Label className="mb-2 block flex items-center gap-2">
                          <Hash className="w-4 h-4" />
                          Hashtags
                        </Label>
                        <div className="flex flex-wrap gap-2">
                          {copyData.hashtags.map((tag, i) => (
                            <Badge key={i} variant="secondary" className="text-blue-400">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-200">
                      <Wand2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Cliquez sur "Générer Copy" pour créer le texte publicitaire</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Platform Selection & Publish */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Send className="w-5 h-5 text-green-400" />
                    Publication
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Platforms */}
                  <div>
                    <Label className="mb-3 block">Plateformes</Label>
                    <div className="flex gap-3">
                      {PLATFORMS.map((platform) => {
                        const Icon = platform.icon;
                        const isSelected = selectedPlatforms.includes(platform.id);
                        return (
                          <button
                            key={platform.id}
                            onClick={() => togglePlatform(platform.id)}
                            className={cn(
                              "flex items-center gap-2 px-4 py-2 rounded-lg transition-all",
                              isSelected
                                ? `${platform.color} text-white`
                                : "bg-zinc-800 text-white hover:bg-zinc-700"
                            )}
                            data-testid={`platform-${platform.id}`}
                          >
                            <Icon className="w-4 h-4" />
                            <span>{platform.name}</span>
                            {isSelected && <Check className="w-4 h-4" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Schedule */}
                  <div>
                    <Label>Planifier (optionnel)</Label>
                    <Input
                      type="datetime-local"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      className="bg-zinc-800 border-zinc-700"
                      data-testid="schedule-input"
                    />
                  </div>

                  {/* Publish Button */}
                  <Button 
                    onClick={publishPost}
                    disabled={publishing || !visualHtml || !copyData}
                    className="w-full bg-green-600 hover:bg-green-700"
                    data-testid="publish-btn"
                  >
                    {publishing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : scheduledDate ? (
                      <Calendar className="w-4 h-4 mr-2" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    {scheduledDate ? 'Planifier' : 'Publier maintenant'}
                  </Button>
                  
                  <p className="text-xs text-gray-200 text-center">
                    Mode MOCK actif — Les publications sont simulées
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
    </AdminPageWrapper>
  );
}

export default AdminSocialPage;
